import React from 'react';
import {css, styled} from 'styled-components';
import DefalutSelectOption from 'components/common/DefalutSelectOption';
import {useFormatMsg} from 'utils/useFormatMsg';
import {minmax} from 'utils/minmax';
import DefaultText from './DefaultText';
import {colors} from 'styles/colors';

/**
 * 라벨 선택 박스 컴포넌트의 Props 인터페이스
 * @property {string} labelText - 라벨 텍스트
 * @property {any} value - 선택된 값
 * @property {any} [setValue] - 값 설정 함수
 * @property {boolean} [isColumn] - 세로 방향 여부
 * @property {Array<{text: string; value: any; color?: string}>} options - 옵션 목록
 * @property {React.CSSProperties} [selectStyle] - 선택 스타일
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [selectContainerStyle] - 선택 컨테이너 스타일
 * @property {React.CSSProperties} [optionTextStyle] - 옵션 텍스트 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {string} [placeholder] - 플레이스홀더
 * @property {boolean} [border] - 테두리 표시 여부
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {React.CSSProperties} [formattedMessageStyle] - 포맷된 메시지 스타일
 * @property {React.Dispatch<React.SetStateAction<HTMLDivElement | null>>} [setObserverRef] - 옵저버 참조 설정 함수
 * @property {number} [webkitLineClamp] - 웹킷 라인 클램프
 * @property {string} [extraButtonText] - 추가 버튼 텍스트
 * @property {React.CSSProperties} [titleWrapStyle] - 제목 래퍼 스타일
 * @property {React.CSSProperties} [extraButtonStyle] - 추가 버튼 스타일
 * @property {React.CSSProperties} [innerStyle] - 내부 스타일
 * @property {() => void} [onClickExtraButton] - 추가 버튼 클릭 이벤트 핸들러
 * @property {React.CSSProperties} [labelInputWrapStyle] - 라벨 입력 래퍼 스타일
 * @property {boolean} [errorMode] - 에러 모드 여부
 */
interface ILabelInputProps {
  labelText: string;
  value: any;
  setValue?: any;
  isColumn?: boolean;
  options: {
    text: string;
    value: any;
    color?: string;
  }[];
  selectStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  selectContainerStyle?: React.CSSProperties;
  optionTextStyle?: React.CSSProperties;
  readOnly?: boolean;
  placeholder?: string;
  border?: boolean;
  buttonStyle?: React.CSSProperties;
  formattedMessageStyle?: React.CSSProperties;
  setObserverRef?: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  webkitLineClamp?: number;
  extraButtonText?: string;
  titleWrapStyle?: React.CSSProperties;
  extraButtonStyle?: React.CSSProperties;
  innerStyle?: React.CSSProperties;
  onClickExtraButton?: () => void;
  labelInputWrapStyle?: React.CSSProperties;
  errorMode?: boolean;
}

/**
 * 라벨 선택 박스 컴포넌트
 * 라벨과 함께 드롭다운 선택 박스를 표시하는 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 선택 박스 컴포넌트의 props
 * @returns {JSX.Element} 라벨 선택 박스 컴포넌트
 */
const LabelSelectBox = ({
  labelText,
  value,
  setValue,
  isColumn,
  options,
  selectStyle,
  labelStyle,
  selectContainerStyle,
  readOnly,
  placeholder,
  border,
  buttonStyle,
  formattedMessageStyle,
  setObserverRef,
  optionTextStyle,
  webkitLineClamp,
  extraButtonText,
  titleWrapStyle,
  extraButtonStyle,
  innerStyle,
  onClickExtraButton,
  labelInputWrapStyle,
  errorMode,
}: ILabelInputProps) => {
  const label = useFormatMsg(labelText) || labelText;

  return (
    <LabelInputContainer>
      <LabelInputWrap $isColumn={isColumn} style={labelInputWrapStyle}>
        <Label htmlFor={label + value} style={labelStyle} $isColumn={isColumn}>
          <DefaultText
            text={labelText}
            formattedMessageStyle={formattedMessageStyle}
          />
          {/* {label} */}
        </Label>
        <DefalutSelectOption
          value={value}
          setValue={value => setValue(value)}
          wrapperStyle={selectStyle}
          id={label}
          options={options}
          placeholderText={placeholder}
          readOnly={readOnly}
          border={border}
          buttonStyle={{
            ...buttonStyle,
            borderColor: errorMode ? colors.primary : colors.borderGray,
          }}
          containerStyle={selectContainerStyle}
          setObserverRef={setObserverRef}
          optionTextStyle={optionTextStyle}
          webkitLineClamp={webkitLineClamp}
          extraButtonText={extraButtonText}
          titleWrapStyle={titleWrapStyle}
          extraButtonStyle={extraButtonStyle}
          innerStyle={innerStyle}
          onClickExtraButton={onClickExtraButton}
        />
      </LabelInputWrap>
      {/* <ErrorTextStyle>{errorText}</ErrorTextStyle> */}
    </LabelInputContainer>
  );
};

export default LabelSelectBox;

const LabelInputContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  height: ${minmax(60)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  word-break: break-all;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  height: 100%;
  padding-right: 2%;
  display: flex;
  justify-content: start;
  align-items: center;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `}
`;
